import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { fetchUserFromSession, type SessionIdentity } from '@/apis/fetchUser';
import {
  fetchOrganizationByName,
  fetchOrganizationByUUID,
} from '@/apis/fetchOrganization';
import type User from '@/types/userType';
import type Organization from '@/types/organizationType';

/**
 * Query-layer plumbing for the identity the server DAL already resolves
 * (`lib/server/dal.ts`). `DashboardIdentity` (`app/(dashboard)/layout.tsx`)
 * prefetches into these keys and `useUserLogged.ts` reads them
 * (`useUserLogged.ts:68-96`, via `serverUserQueryOptions` /
 * `serverOrgQueryOptions` / `serverOrgByNameQueryOptions`), so identity is on
 * the client cache from the first render. These options are therefore load
 * bearing, not staging: changing a key or a return shape here changes what
 * every dashboard route sees before it has fetched anything.
 *
 * Secrets guardrail: these entries hold ONLY the unmerged backend row. The
 * FE types carry `User.accessToken` (types/userType.ts:15) and
 * `Organization.ApiKey` (types/organizationType.ts:77), but neither field
 * exists on the backend IDL dataclasses these endpoints serialize from, so
 * `fetchUser/` and `fetchOrganization/` never return them regardless of
 * caller. The backend IDL is the source of truth for that, not these types.
 * Nothing may ever `setQueryData` a session-merged user (one with a
 * client-attached `accessToken`) into these keys.
 */

export const serverUserQueryKey = ['serverUser'] as const;
export const serverOrgQueryKey = ['serverOrg'] as const;

/**
 * Client-side counterpart to `getServerUser()` (`lib/server/dal.ts`).
 *
 * `getServerUser` is `'server-only'` and cannot run in the browser, so a
 * cache-miss/refetch on `serverUserQueryKey` in the browser resolves through
 * `fetchUserFromSession` instead -- the same GitHub-id-first resolution
 * order the DAL uses. Its result is normalized from `User | undefined` to
 * `User | null` to match `getServerUser`'s return shape exactly; otherwise a
 * refetch would silently swap in a differently-shaped value than the one the
 * server hydrated.
 */
export function serverUserQueryOptions(
  sessionUser: SessionIdentity | null | undefined
) {
  return queryOptions({
    queryKey: serverUserQueryKey,
    queryFn: async (): Promise<User | null> => {
      const user = await fetchUserFromSession(sessionUser);
      return user ?? null;
    },
    staleTime: 180_000,
  });
}

/**
 * Client-side counterpart to `getServerOrganization()` (`lib/server/dal.ts`).
 *
 * `getServerOrganization` returns `Organization | null` unwrapped, but
 * `fetchOrganizationByUUID` (`apis/fetchOrganization.ts`) resolves to
 * `{ Org: Organization }` (see its existing consumer, `state/orgState.ts`,
 * reading `.Org`). Unwrapping here is required, not optional: without it a
 * cache-miss/refetch would silently yield a differently-shaped value than
 * what the server prefetch hydrated, and nothing would error.
 */
export function serverOrgQueryOptions(orgUUID: string | undefined) {
  return queryOptions({
    queryKey: serverOrgQueryKey,
    queryFn: async (): Promise<Organization | null> => {
      if (!orgUUID) return null;
      const response = await fetchOrganizationByUUID(orgUUID);
      return response?.Org ?? null;
    },
    staleTime: 180_000,
  });
}

/**
 * Client-only counterpart to the `localStorage.OrgName` fallback in
 * `UserContext.fetchOrganization` / `useUpdatedData`. The server DAL cannot
 * run this path (`dal.ts` has no name to resolve from), so a user whose row
 * predates `OrganizationUUID` would otherwise look org-less and get bounced
 * to `/onboarding`.
 */
export function serverOrgByNameQueryOptions(orgName: string) {
  return queryOptions({
    queryKey: [...serverOrgQueryKey, 'byName', orgName] as const,
    queryFn: async (): Promise<Organization | null> => {
      if (!orgName) return null;
      const org = await fetchOrganizationByName(orgName);
      return org ?? null;
    },
    staleTime: 180_000,
  });
}

/**
 * Write a freshly-resolved identity straight into the query cache, in the
 * same shape `getServerUser`/`getServerOrganization` produce.
 *
 * `serverUserQueryKey`/`serverOrgQueryKey` are fixed strings (no per-user
 * segment), so a stale cache entry never naturally refetches just because a
 * new user session started -- `staleTime` is 3 minutes and nothing else
 * invalidates these keys. `useUserLogged()` (`useUserLogged.ts`) reads
 * straight from this cache now, so the two non-teardown identity writers
 * that used to be zustand-only (`WorkspaceSetup.tsx`'s `hydrateUserAndOrg`,
 * `JoinWorkspaceOrgPageContent.tsx`'s `goHomeAfterJoin`) must call this or a
 * user who just created/joined a workspace reads back as org-less and gets
 * bounced to `/onboarding` by `DashboardLayoutClient`.
 *
 * Organization is normalized to `null` when it carries no real identity
 * (no `OrgUUID`/`OrgName`) rather than writing the zustand org store's
 * `emptyOrganization` sentinel verbatim, so a cache read here matches
 * `getServerOrganization`'s `Organization | null` contract exactly.
 */
export function seedServerIdentityCache(
  queryClient: QueryClient,
  user: User | null,
  organization: Organization | null | undefined
): void {
  queryClient.setQueryData(serverUserQueryKey, user);
  const hasOrg = !!(organization?.OrgUUID || organization?.OrgName);
  queryClient.setQueryData(serverOrgQueryKey, hasOrg ? organization : null);
}
